<template>
  <div class="international-address-field">
    <div :class="classes.selectHolder" v-if="showCountrySelect">
      <label :class="classes.label" v-if="countryLabel">{{ countryLabel }}</label>
      <select :class="classes.select" :value="countryCode" @change="onCountryChange">
        <option v-for="option in countries" :key="option.code" :value="option.code">{{ option.label }}</option>
      </select>
    </div>

    <!--
      NL: the EXISTING pdok-address-typeahead, completely unchanged - same props/events, same
      component instance type. Zero behaviour change for the Dutch flow this app already relies
      on; this wrapper only decides WHETHER to render it.
    -->
    <pdok-address-typeahead
      v-if="isNL"
      :postcode="postcode"
      :house-number="houseNumber"
      :addition="addition"
      :suggest-url="suggestUrl"
      :resolve-url="resolveUrl"
      :suggest-postcode-url="suggestPostcodeUrl"
      :resolve-postcode-url="resolvePostcodeUrl"
      :locale="locale"
      @resolved="onPdokResolved"
      @resolved-postcode="onPdokResolvedPostcode"
      @unavailable="onProviderUnavailable"
    ></pdok-address-typeahead>

    <google-address-typeahead
      v-else-if="isGoogleSupported"
      :country-code="countryCode"
      :suggest-url="googleSuggestUrl"
      :validate-url="googleValidateUrl"
      :locale="locale"
      :theme="theme"
      @resolved="onGoogleResolved"
      @unavailable="onProviderUnavailable"
    ></google-address-typeahead>

    <p v-else-if="countryCode" class="text-sm mt-1" style="color: #777">
      {{ manualNotice }}
    </p>
  </div>
</template>

<script>
export default {
  name: 'InternationalAddressField',
  props: {
    countryCode: { type: String, default: 'NL' },
    countries: { type: Array, default: function () { return []; } },
    googleSupportedCountries: { type: Array, default: function () { return []; } },
    showCountrySelect: { type: Boolean, default: true },
    // NL/PDOK passthrough props - identical contract to PdokAddressTypeahead.vue itself.
    postcode: { type: String, default: '' },
    houseNumber: { type: String, default: '' },
    addition: { type: String, default: '' },
    suggestUrl: { type: String, default: '' },
    resolveUrl: { type: String, default: '' },
    suggestPostcodeUrl: { type: String, default: '' },
    resolvePostcodeUrl: { type: String, default: '' },
    // Google endpoints.
    googleSuggestUrl: { type: String, default: '' },
    googleValidateUrl: { type: String, default: '' },
    locale: { type: String, default: 'nl' },
    // See GoogleAddressTypeahead.vue's own docblock - 'public' or 'admin', passed straight
    // through to it; also used for this wrapper's own country-select styling.
    theme: { type: String, default: 'public' },
  },
  computed: {
    isNL() {
      return (this.countryCode || 'NL').toUpperCase() === 'NL';
    },
    isGoogleSupported() {
      return !this.isNL && this.googleSupportedCountries.indexOf((this.countryCode || '').toUpperCase()) !== -1;
    },
    countryLabel() {
      return this.locale === 'en' ? 'Country' : 'Land';
    },
    manualNotice() {
      return this.locale === 'en'
        ? 'Address suggestions are not available for this country - please enter the address manually below.'
        : 'Adressuggesties zijn niet beschikbaar voor dit land - vul het adres hieronder handmatig in.';
    },
    classes() {
      if (this.theme === 'admin') {
        return { selectHolder: 'v-input', label: 'v-input__label', select: 'v-input__field fontawesome-select' };
      }
      return { selectHolder: 'theme-input__select-holder', label: 'theme-input__label', select: 'theme-input__field fontawesome-select' };
    },
  },
  methods: {
    onCountryChange(event) {
      this.$emit('country-changed', event.target.value);
    },
    onPdokResolved(resolved) {
      this.$emit('resolved', resolved);
    },
    onPdokResolvedPostcode(resolved) {
      this.$emit('resolved-postcode', resolved);
    },
    onGoogleResolved(address) {
      // Deliberately a DIFFERENT event name from the PDOK branch's 'resolved'/'resolved-postcode'
      // above: those carry PDOK's own raw, BAG-identifier-bearing shape (needed unchanged by the
      // existing onPdokAddressResolved handlers for energy-label lookup etc.), while this always
      // carries the provider-neutral NormalizedAddress shape (postal_code, house_number, locality,
      // ...) GoogleAddressTypeahead.vue's own 'resolved' event already emits flat - conflating
      // the two under one event name would silently break whichever handler expected the other
      // shape, so the parent wires a SEPARATE @international-resolved handler for this one, used
      // only for the Google-supported-country branch.
      this.$emit('international-resolved', address);
    },
    onProviderUnavailable() {
      this.$emit('unavailable');
    },
  },
};
</script>
